import {
    Address,
    AppointmentFieldsFragment,
    AvailabilityDayFieldsFragment,
    AvailabilityHours,
    AvailabilityHoursInput,
    AvailabilityInput,
    ClinicFieldsFragment,
    ClinicianFieldsFragment,
    EntityOrigin,
    PatientFieldsFragment,
    Species,
    TimeInterval,
    TimeIntervalInput,
} from '~graphql/generated/graphql'
import { UsStates } from '~utils/constants/states'
import queryString from 'query-string'
import {
    addDays,
    addHours,
    differenceInDays,
    differenceInWeeks,
    differenceInYears,
    endOfDay,
    format,
    getDay,
    startOfDay,
} from 'date-fns'
import { differenceInMonths } from 'date-fns/esm'
import { formatInTimeZone, utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz'
import pluralize from 'pluralize'
import { DEFAULT_TIME_ZONE } from '~config/constants'
import { LocalStorageKeys } from '~config/constants'
import { DateFormat } from './shared-types'
import { AvailabilityWithId } from '~components/shared/adjusted-availability/constants'

export function sortPatientsByName(
    a: Pick<PatientFieldsFragment, 'name'>,
    b: Pick<PatientFieldsFragment, 'name'>,
): number {
    const nameA = a?.name?.toUpperCase() // ignore upper and lowercase
    const nameB = b?.name?.toUpperCase() // ignore upper and lowercase

    if (!nameA || !nameB) return 0

    if (nameA < nameB) {
        return -1
    }
    if (nameA > nameB) {
        return 1
    }

    // names must be equal
    return 0
}

export const getAddressString = (
    address: Address | null | undefined,
): string => {
    if (!address) return 'No address listed.'
    return Object.values({
        a: formatStreetA(address.street_a),
        b: address.street_b,
        city: address.city,
        state: getStateAbbreviation(address.state || ''),
        zip: address.zip,
    })
        .filter(v => !!v)
        .join(', ')
}

export const getUrlQueryParamsObj = (): queryString.ParsedQuery => {
    if (typeof window === 'undefined') {
        return {}
    }
    const { search } = window.location
    return queryString.parse(search)
}

export const getStateAbbreviation = (state: string): string => {
    return UsStates.find(s => s.name === state)?.abbreviation || ''
}

export const getAge = (birthday: Date | undefined) => {
    if (!birthday) return
    const numYears = differenceInYears(new Date(), birthday)
    if (numYears > 0) {
        return `${numYears} ${pluralize('year', numYears)}`
    }

    const numMonths = differenceInMonths(new Date(), birthday)
    if (numMonths > 0) {
        return `${numMonths} ${pluralize('month', numMonths)}`
    }

    const numWeeks = differenceInWeeks(new Date(), birthday)
    if (numWeeks > 0) {
        return `${numWeeks} ${pluralize('week', numWeeks)}`
    }

    const numDays = differenceInDays(new Date(), birthday)
    return `${numDays} ${pluralize('day', numDays)}`
}

export const getPatientFallback = (species: Species | null | undefined) => {
    if (!species)
        return 'https://static.wikia.nocookie.net/theislands/images/4/48/Cat_Sil_Placeholder.jpg/revision/latest?cb=20140101201417'

    if (
        [
            Species.Amphibian,
            Species.Cow,
            Species.Fish,
            Species.Goat,
            Species.Marsupial,
            Species.Pig,
            Species.Primate,
            Species.Sheep,
            Species.Other,
        ].includes(species)
    ) {
        return ''
    }

    // Turn the above into a switch case
    switch (species) {
        case Species.Dog:
            return 'data:image/png;base64,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'
        case Species.Cat:
        case Species.Ferret:
        case Species.Horse:
        case Species.PocketPet:
        case Species.Rabbit:
        case Species.Reptile:
        case Species.Rodent:
            return 'https://static.wikia.nocookie.net/theislands/images/4/48/Cat_Sil_Placeholder.jpg/revision/latest?cb=20140101201417'
        default:
            return 'https://static.wikia.nocookie.net/theislands/images/4/48/Cat_Sil_Placeholder.jpg/revision/latest?cb=20140101201417'
    }
}

export const validateEmail = (email: string) => {
    return !!String(email)
        .toLowerCase()
        .match(
            /[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?/g,
        )
}

export const validatePhone = (phone: string) => {
    return String(phone).match(
        /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im,
    )
}

export const errorStyle = (isError: boolean) => {
    return {
        borderColor: isError ? 'danger.300' : undefined,
        labelProps: { color: isError ? 'danger.300' : 'brand.500' },
    }
}

export const getCleanEnum = (value: string | null | undefined) => {
    if (!value) return ''
    return value.replace(/_/g, ' ').replace(/[A-Z]/g, ' $&').trim()
}

export function splitArrayWithFilter<T>(
    array: T[],
    boolCheck: (item: T) => boolean,
) {
    const [arr1, arr2] = array.reduce(
        ([arr1, arr2]: [T[], T[]], item) => {
            return boolCheck(item)
                ? [[...arr1, item], arr2]
                : [arr1, [...arr2, item]]
        },
        [[], []],
    )

    return [arr1, arr2]
}

// Returns an array of arrays (of length boolChecks.length + 1)
// split by the consecutive filters (boolChecks) passed in
// The last array is the items that don't satisfy any of the filters
// Ex. splitArrayWithFilters([1,2,3,4,5], [i => i < 3, i => i > 3]) => [[1,2],[4,5],[3]]
export const splitArrayWithFilters = <T>(
    array: T[],
    boolChecks: ((item: T) => boolean)[],
): T[][] => {
    const retArr: T[][] = [...boolChecks.map(() => []), []]

    array.forEach(item => {
        const index = boolChecks.findIndex(check => check(item))
        if (index === -1) {
            retArr[retArr.length - 1].push(item)
        } else {
            retArr[index].push(item)
        }
    })

    return retArr
}

enum DayOfWeek {
    MONDAY = 'monday',
    TUESDAY = 'tuesday',
    WEDNESDAY = 'wednesday',
    THURSDAY = 'thursday',
    FRIDAY = 'friday',
    SATURDAY = 'saturday',
    SUNDAY = 'sunday',
}

export function getHoursForDate(
    clinicians: ClinicianFieldsFragment[],
    date: Date,
) {
    const day = format(date, 'EEEE').toLowerCase() as DayOfWeek
    const hours = clinicians.reduce((acc, clinician) => {
        let total = 0
        if (clinician.hours) {
            if (clinician.hours[day]) {
                clinician.hours[day]?.forEach(interval => {
                    total += (interval.end || 0) - (interval.start || 0)
                })
            }
        }

        return acc + total
    }, 0)

    return hours
}

export function adjustArrayWithItem<T>(
    array: T[],
    item: T,
    boolCheck?: (item: T) => boolean,
) {
    if (boolCheck ? array.some(i => boolCheck(i)) : array.includes(item)) {
        return array.filter(i => (boolCheck ? !boolCheck(i) : i !== item))
    } else {
        return [...array, item]
    }
}

export const startOfDayWithTimeZone = (
    date: Date | number,
    timeZone: string | null | undefined,
) => {
    let timeZoneToUse = timeZone || 'America/New_York'

    const inputZoned = utcToZonedTime(date, timeZoneToUse)

    const dayStartZoned = startOfDay(inputZoned)

    const dayStart = zonedTimeToUtc(dayStartZoned, timeZoneToUse)

    return dayStart
}

export const endOfDayWithTimeZone = (
    date: Date | number,
    timeZone: string | null | undefined,
) => {
    let timeZoneToUse = timeZone || 'America/New_York'

    const inputZoned = utcToZonedTime(date, timeZoneToUse)

    const dayEndZoned = endOfDay(inputZoned)

    const dayEnd = zonedTimeToUtc(dayEndZoned, timeZoneToUse)

    return dayEnd
}

export const dayConverter: (
    | 'sunday'
    | 'monday'
    | 'tuesday'
    | 'wednesday'
    | 'thursday'
    | 'friday'
    | 'saturday'
)[] = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
]

export const getClinicOpenAndClose = (
    date: Date,
    clinic: ClinicFieldsFragment,
) => {
    const intervals = clinic?.hours?.[dayConverter[getDay(date)]] || []

    const start =
        intervals.length > 0
            ? (intervals[0].start as number | undefined)
            : undefined

    const end =
        intervals.length > 0
            ? (intervals[intervals.length - 1].end as number | undefined)
            : undefined

    return { start, end }
}

export const getStartOfClinicWorkday = (
    date: Date,
    clinic: ClinicFieldsFragment,
) => {
    const zonedStartOfDay = startOfDayWithTimeZone(
        date,
        clinic.time_zone || 'America/New_York',
    )

    const intervals =
        clinic?.hours?.[dayConverter[getDay(zonedStartOfDay)]] || []

    intervals.length > 0 ? intervals[0].start : 6

    const startOfClinicWorkDay = addHours(
        zonedStartOfDay,
        intervals.length > 0 ? intervals[0].start || 0 : 6,
    )

    return startOfClinicWorkDay
}

export function cleanTypenames<T extends object>(
    obj: T,
): Omit<T, '__typename'> {
    if (
        typeof obj === 'string' ||
        typeof obj === 'number' ||
        typeof obj === 'boolean'
    )
        return obj
    if (Array.isArray(obj)) return obj.map(cleanTypenames) as any
    const keys = Object.keys(obj) as (keyof T)[]
    const newObj: any = {}
    keys.forEach(key => {
        if (key === '__typename') return
        const value = obj[key]
        if (Array.isArray(value)) {
            newObj[key] = value.map(v => cleanTypenames(v))
        } else if (typeof value === 'object' && value !== null) {
            newObj[key] = cleanTypenames(value)
        } else {
            newObj[key] = value
        }
    })
    return newObj
}

export function cleanFields<T extends object>(obj: T, fields: string[]): T {
    if (
        typeof obj === 'string' ||
        typeof obj === 'number' ||
        typeof obj === 'boolean'
    )
        return obj
    if (Array.isArray(obj)) return obj.map(o => cleanFields(o, fields)) as any
    const keys = Object.keys(obj) as (keyof T)[]
    const newObj: any = {}
    keys.forEach(key => {
        if (fields.includes(key.toString())) return
        const value = obj[key]
        if (Array.isArray(value)) {
            newObj[key] = value.map(v => cleanFields(v, fields))
        } else {
            newObj[key] = value
        }
    })
    return newObj
}

export function cleanTypenamesOrUndefined<T extends object>(
    obj: T | null | undefined,
): Omit<T, '__typename'> | undefined {
    if (!obj) return undefined
    return cleanTypenames(obj)
}

export const getClinicianIsAvailable = (clinician: ClinicianFieldsFragment) => {
    return Boolean(
        clinician.is_assignable_in_pms &&
            clinician.is_active &&
            clinician.hours &&
            getHasAvailability(clinician.hours),
    )
}

export const availabilityExists = (availability: AvailabilityHours) => {
    const cleanHours = getCleanHoursObject(availability)
    return Object.keys(cleanHours).some(a => cleanHours[a].length > 0)
}

const dayOrder = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
]

export function cleanIdFromAdjustedAvailability(
    availability: AvailabilityWithId[],
): AvailabilityInput[] {
    return availability.map(a => {
        const { id, ...rest } = a
        return rest
    })
}

export const getCleanHoursObject = (availability: AvailabilityHoursInput) => {
    let hoursObj: {
        [key: string]: TimeIntervalInput[]
    } = {}

    const availKeys = [...Object.keys(availability || [])]

    availKeys
        .sort(
            (a, b) =>
                dayOrder.findIndex(d => d === a) -
                dayOrder.findIndex(d => d === b),
        )
        .map(key => {
            if (!availability || key === '__typename') return
            let intervals =
                availability[key as keyof AvailabilityHoursInput] || []
            if (typeof intervals === 'string') return
            intervals.forEach(interval => {
                let obj = { ...cleanTypenames(interval) }

                hoursObj[key] = [...(hoursObj[key] || []), obj]
            })
        })
    return hoursObj
}

export function getHasAvailability(hours: AvailabilityDayFieldsFragment) {
    return !Object.keys(hours).every(day => {
        if (day === '__typename') return true

        const intervals = hours[day as keyof typeof hours]
        if (!intervals || intervals.length === 0) return true
        return false
    })
}

// write a function to format a string to a money format with commas
export const formatMoney = (amount: number) => {
    return amount.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    })
}

export function formatCents(amountInCents: number) {
    return formatMoney(amountInCents / 100)
}

export function formatPhoneForFirebaseAuth(phone: string) {
    // This function accepts a raw phone number string and returns it in the format +1 999-999-9999
    const cleaned = ('' + phone).replace(/\D/g, '')
    const match = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/)
    if (match) {
        return '+' + match[1] + ' ' + match[2] + '-' + match[3] + '-' + match[4]
    }
    return phone
}

export function formatPhoneNumber(phone: string | null | undefined) {
    const isDemoMode = getIsDemoMode()
    if (isDemoMode) return '+1 (555) 555-5555'

    const cleaned = ('' + phone).replace(/\D/g, '')
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/)
    const matchWithAreaCode = cleaned.match(/^(\d{1})(\d{3})(\d{3})(\d{4})$/)
    if (match) {
        return '(' + match[1] + ') ' + match[2] + '-' + match[3]
    }
    if (matchWithAreaCode) {
        return (
            '+' +
            matchWithAreaCode[1] +
            ' (' +
            matchWithAreaCode[2] +
            ') ' +
            matchWithAreaCode[3] +
            '-' +
            matchWithAreaCode[4]
        )
    }
    return phone
}

export function cleanPhoneNumber(phone?: string) {
    const cleaned = ('' + phone).replace(/\D/g, '')
    return cleaned
}

export function removeCountryCode(phone: string) {
    return phone.slice(1)
}

const formatter = [
    { value: 1e6, symbol: 'M' },
    { value: 1e3, symbol: 'k' },
    { value: 1, symbol: '' },
]
const rx = /\.0+$|(\.[0-9]*[1-9])0+$/

export const formatMoneyShort = (amount: number) => {
    const item = formatter.find(item => amount >= item.value)

    return item
        ? (amount / item.value).toFixed(1).replace(rx, '$1') + item.symbol
        : amount.toFixed(2)
}

export const roundFloat = (amount: number, precision: number = 2) => {
    return parseFloat(amount.toFixed(precision))
}

// export const formatMoney = (money: number, showDecimals?: boolean) => {
//     return (showDecimals ? money.toFixed(2) : money.toString()).replace(
//         /\d(?=(\d{3})+\.)/g,
//         '$&,'
//     )
// }

export const formatDateOrDateRange = (startDate: Date, endDate?: Date) => {
    if (endDate) {
        return `${format(startDate, 'M/d/yyyy')} - ${format(
            endDate,
            'M/d/yyyy',
        )}`
    } else {
        return format(startDate, 'M/d/yyyy')
    }
}

export const getDefaultHours = (clinic: ClinicFieldsFragment | undefined) => {
    const hours: { [key: string]: TimeInterval[] } = {
        monday: clinic?.hours?.monday || [],
        tuesday: clinic?.hours?.tuesday || [],
        wednesday: clinic?.hours?.wednesday || [],
        thursday: clinic?.hours?.thursday || [],
        friday: clinic?.hours?.friday || [],
        saturday: clinic?.hours?.saturday || [],
        sunday: clinic?.hours?.sunday || [],
    }

    return hours
}

// Get last day clinic was open
export const getLastOpenDayForClinic = (
    clinic: ClinicFieldsFragment | undefined,
) => {
    const hours = getDefaultHours(clinic)
    const today = new Date()
    let lastOpenDay = today

    for (let i = 1; i <= 7; i++) {
        const dayToCheck = addDays(today, -i)
        const stringDay = format(dayToCheck, 'EEEE').toLowerCase()

        if (hours[stringDay].length > 0) {
            lastOpenDay = dayToCheck
            break
        }
    }

    return lastOpenDay
}

// Get next day clinic is open
export const getNextOpenDayForClinic = (
    clinic: ClinicFieldsFragment | undefined,
) => {
    const hours = getDefaultHours(clinic)
    const today = new Date()
    let nextOpenDay = today

    for (let i = 1; i <= 7; i++) {
        const dayToCheck = addDays(today, i)
        const stringDay = format(dayToCheck, 'EEEE').toLowerCase()

        if (hours[stringDay].length > 0) {
            nextOpenDay = dayToCheck
            break
        }
    }

    return nextOpenDay
}

export const between0And100 = (value: number): number => {
    return Math.max(0, Math.min(100, value))
}

export const dedupeArray = <T>(array: T[], fieldToCheck?: keyof T): T[] => {
    if (!array.length) return []

    if (fieldToCheck) {
        const seen = new Set()
        return array.filter(item => {
            const key = item[fieldToCheck]
            if (seen.has(key)) {
                return false
            } else {
                seen.add(key)
                return true
            }
        })
    } else {
        const seen = new Set()
        return array.filter(item => {
            if (seen.has(item)) {
                return false
            } else {
                seen.add(item)
                return true
            }
        })
    }
}

export function fastDedupeArrayFunction<T>(
    array: T[],
    checkFunction: (item: T) => string,
): T[] {
    if (!array.length) return []

    const seen = new Set()
    return array.filter(item => {
        const key = checkFunction(item)
        if (seen.has(key)) {
            return false
        } else {
            seen.add(key)
            return true
        }
    })
}

export const groupArray = <T>(
    array: T[],
    groupCheck: (item: T) => string,
): { [key: string]: T[] } => {
    const grouped: { [key: string]: T[] } = {}

    array.forEach(item => {
        if (grouped[groupCheck(item)]) {
            grouped[groupCheck(item)].push(item)
        } else {
            grouped[groupCheck(item)] = [item]
        }
    })

    return grouped
}

export const stringIfExists = (
    conditionalString: string | null | undefined,
    options?: { pretext?: string; posttext?: string },
): string => {
    const { pretext, posttext } = options || {}

    return conditionalString
        ? `${pretext || ''}${conditionalString}${posttext || ''}`
        : ''
}

export function csArray(items: string[]): string {
    return `{${items.map(item => `"${item}"`).join(',')}}`
}

export const cleanPhone = (phone: string | null | undefined): string => {
    return ('' + phone).replace(/\D/g, '')
}

export function toggleDemoMode() {
    if (typeof window === 'undefined') {
        return { isDemoMode: false }
    }
    const isDemoMode = getIsDemoMode()
    const sessionStorageKey = LocalStorageKeys.isDemoMode
    const newIsDemoMode = !isDemoMode
    sessionStorage.setItem(sessionStorageKey, newIsDemoMode.toString())

    return { isDemoMode: newIsDemoMode }
}

export function getIsDemoMode() {
    if (typeof window === 'undefined') {
        return false
    }
    const sessionStorageKey = LocalStorageKeys.isDemoMode

    return sessionStorage.getItem(sessionStorageKey) === 'true'
}

export function formatEmail(email: string | null | undefined) {
    const isDemoMode = getIsDemoMode()

    if (isDemoMode) {
        // get seeded first name
        const firstName = getRandomFirstName(email || 'default')
        // get seeded last name
        const lastName = getRandomLastName(email || 'default')
        // return email with seeded name
        return `${firstName}.${lastName}@example.com`.toLowerCase()
    }

    if (!email) return null
    return email.toLowerCase()
}

export function formatClinicianName(name: string | null | undefined) {
    const isDemoMode = getIsDemoMode()
    if (isDemoMode) {
        return `Dr. ${getRandomName(name || 'default')}`
    }
    return name
}

export function formatClinicName(name: string | null | undefined) {
    const isDemoMode = getIsDemoMode()
    if (isDemoMode) {
        return getRandomVetClinicName(name || 'default')
    }
    return name
}

export function formatStreetA(streetA: string | null | undefined) {
    const isDemoMode = getIsDemoMode()
    if (isDemoMode) {
        return getRandomStreetA(streetA || 'default')
    }
    return streetA
}

export function formatCity(city: string | null | undefined) {
    const isDemoMode = getIsDemoMode()
    if (isDemoMode) {
        return getRandomCity(city || 'default')
    }
    return city
}

export function formatZip(zip: string | null | undefined) {
    const isDemoMode = getIsDemoMode()
    if (isDemoMode) {
        return '80200'
    }
    return zip
}

export const formatPhoneIfValid = (
    phone: string | null | undefined,
): string | null => {
    const cleaned = cleanPhone(phone)

    if (cleaned.length < 10) return null

    return formatPhoneNumber(phone) || null
}

export const getAlivePatients = <
    T extends { is_deceased?: boolean | null | undefined },
>(
    patients: T[],
) => patients.filter(p => !p.is_deceased)

export const removeTypename = <T extends { __typename?: string | undefined }>(
    obj: T,
): T => {
    const newObj = { ...obj }
    delete newObj.__typename
    return newObj
}

// Updating item at array in index
export const updateItemAtIndex = <T>(
    array: T[],
    item: T,
    index: number,
): T[] => {
    return [...array.slice(0, index), item, ...array.slice(index + 1)]
}

export function getBasePhoneNumber(
    phone: string | null | undefined,
): string | undefined {
    return phone?.replace(/\D/g, '').slice(-10)
}

export function sortByField<T>(
    arr: T[],
    field: keyof T | ((item: T) => any),
    options?: {
        alwaysLast?: string[]
    },
): T[] {
    return [...arr].sort((a, b) => {
        const aField = typeof field === 'function' ? field(a) : a[field]
        const bField = typeof field === 'function' ? field(b) : b[field]

        if (options?.alwaysLast) {
            if (options.alwaysLast.includes(aField as string)) return 1
            if (options.alwaysLast.includes(bField as string)) return -1
        }

        if (aField < bField) return -1
        if (aField > bField) return 1
        return 0
    })
}

export const checkForChckupOrigin = (
    origin: EntityOrigin | null | undefined,
): boolean => {
    if (!origin) return false
    return [
        EntityOrigin.ChckupDashboard,
        EntityOrigin.ChckupWebsite,
        EntityOrigin.ChckupWidget,
    ].includes(origin)
}

export function getExisting<T>(array: T[]): NonNullable<T>[] {
    return array.filter(Boolean) as NonNullable<T>[]
}

export function hexToLinearGradient(hexColor: string): string {
    const colorDiff = 40

    let r: number, g: number, b: number
    hexColor = hexColor.replace('#', '')

    // Convert to RGB values
    if (hexColor.length === 3) {
        // shorthand notation
        r = parseInt(hexColor[0] + hexColor[0], 16)
        g = parseInt(hexColor[1] + hexColor[1], 16)
        b = parseInt(hexColor[2] + hexColor[2], 16)
    } else {
        // full notation
        r = parseInt(hexColor.substring(0, 2), 16)
        g = parseInt(hexColor.substring(2, 4), 16)
        b = parseInt(hexColor.substring(4, 6), 16)
    }

    // Generate lighter and darker shades
    const lightColor = `rgb(${Math.min(r + colorDiff, 255)}, ${Math.min(
        g + colorDiff,
        255,
    )}, ${Math.min(b + colorDiff, 255)})`
    const darkColor = `rgb(${Math.max(r - colorDiff, 0)}, ${Math.max(
        g - colorDiff,
        0,
    )}, ${Math.max(b - colorDiff, 0)})`

    // Create CSS gradient
    const gradient = `linear-gradient(to-b, ${lightColor}, #${hexColor}, ${darkColor})`

    return gradient
}

export function isValidHexColor(color: string | null | undefined) {
    if (!color) return false

    return /^#[0-9A-F]{6}$/i.test(color)
}

export function formatDate(
    date: Date | number,
    format: DateFormat = DateFormat.Date,
    timeZone: string = DEFAULT_TIME_ZONE,
) {
    return formatInTimeZone(date, timeZone, format)
}

export function stringToColor(str: string) {
    const colors = [
        'red',
        'orange',
        'yellow',
        'green',
        'teal',
        'blue',
        'purple',
    ]

    const hash = str
        .split('')
        .reduce((acc, char) => acc + char.charCodeAt(0), 0)

    return colors[hash % colors.length]
}

export function getHumanReadablePropName(field: string): string {
    return field.replace('_', ' ')
}

export function getBookingEntityValue(
    doesUseRoomsToSchedule: boolean,
    doesUseCliniciansToSchedule: boolean,
) {
    if (doesUseRoomsToSchedule && doesUseCliniciansToSchedule) {
        return 'Clinician & Room'
    } else if (doesUseRoomsToSchedule) {
        return 'Room'
    } else if (doesUseCliniciansToSchedule) {
        return 'Clinician'
    } else {
        return undefined
    }
}

export function compareObjects(
    a: object | null | undefined,
    b: object | null | undefined,
    clean?: (obj: object) => object,
) {
    if (!a) {
        return !b
    }

    if (!b) {
        return true
    }

    const cleanA = clean ? clean(a) : a
    const cleanB = clean ? clean(b) : b

    return JSON.stringify(cleanA) !== JSON.stringify(cleanB)
}

export function convertRichTextToPlainText(richText: string): string {
    // Remove HTML tags
    let plainText = richText.replace(/<[^>]+>/g, '')

    // Decode HTML entities
    plainText = plainText
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")

    // Replace <br> and </p> tags with newlines
    plainText = plainText
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n\n')

    // Remove extra whitespace
    plainText = plainText.replace(/\s+/g, ' ').trim()

    // Restore line breaks
    plainText = plainText.replace(/\n /g, '\n').replace(/\n+/g, '\n')

    return plainText
}

export default function generatePublicFormLink(formId: string): string {
    const baseUrl = process.env.GATSBY_PORTAL_URL as string

    return `${baseUrl}/form/fill/${formId}`
}

export function getPortalLink(
    appointment: Pick<AppointmentFieldsFragment, 'id'>,
) {
    return `https://portal.chckup.app/appointments/${appointment.id}`
}

export const getClientFullName = (
    client:
        | {
              first_name?: string | null | undefined
              last_name?: string | null | undefined
          }
        | undefined
        | null,
    shouldReturnNull: boolean = false,
): string | null => {
    if (!client) return shouldReturnNull ? null : 'Name not found'

    const isDemoMode = getIsDemoMode()
    if (isDemoMode) {
        // Generate a seed based on the client's name.
        const seed =
            `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim()
        return getRandomName(seed || 'default') // Fallback seed if both names are empty
    }

    let fullName = ''
    if (client.first_name) {
        fullName += client.first_name
    }

    if (client.last_name) {
        fullName += ` ${client.last_name}`
    }

    return fullName || (shouldReturnNull ? null : 'Name not found')
}

const firstNames = [
    'John',
    'Barry',
    'Jane',
    'Colin',
    'Timothy',
    'Sarah',
    'Stacy',
    'Nancy',
    'Susan',
    'David',
    'Alice',
    'Robert',
    'Emily',
    'Michael',
    'Christopher',
    'Patricia',
    'Jennifer',
    'Linda',
    'Barbara',
    'Elizabeth',
    'William',
    'Aiden',
    'Olivia',
]

const lastNames = [
    'Smith',
    'Johnson',
    'Williams',
    'Jones',
    'Brown',
    'Davis',
    'Miller',
    'Wilson',
    'Moore',
    'Taylor',
    'Anderson',
    'Thomas',
    'Jackson',
    'White',
    'Harris',
    'Martin',
    'Thompson',
    'Garcia',
    'Martinez',
    'Robinson',
    'Clark',
    'Rodriguez',
    'Lewis',
    'Lee',
    'Walker',
    'Hall',
]

function getRandomName(seed: string): string {
    const firstName = getRandomFirstName(seed)
    const lastName = getRandomLastName(seed)
    return `${firstName} ${lastName}`
}

function getRandomFirstName(seed: string): string {
    const random = customSeededRandom(seed)
    return firstNames[Math.floor(random() * firstNames.length)]
}

function getRandomLastName(seed: string): string {
    const random = customSeededRandom(seed)
    return lastNames[Math.floor(random() * lastNames.length)]
}

const ClinicCities = [
    'Wolverton',
    'Oakbridge',
    'Springfield',
    'Dallas',
    'Memphis',
    'Nashville',
    'Atlanta',
    'Chicago',
    'Los Angeles',
    'New York',
    'Miami',
    'San Francisco',
    'Seattle',
    'Washington D.C.',
    'Boston',
    'Philadelphia',
    'San Diego',
]

const ClinicSuffix = [
    'Animal Hospital',
    'Vet Clinic',
    'Veterinary Associates',
    'Veterinary Partners',
    'Urgent Care',
    'Pet Veterinarians',
    'Pet Care',
    'Pet Hospital',
]

function getRandomCity(seed: string) {
    const random = customSeededRandom(seed)
    return ClinicCities[Math.floor(random() * ClinicCities.length)]
}

function getRandomVetClinicName(seed: string) {
    const random = customSeededRandom(seed)
    const city = getRandomCity(seed)
    const suffix = ClinicSuffix[Math.floor(random() * ClinicSuffix.length)]
    return `${city} ${suffix}`
}

const streetAs = [
    '123 Main Street',
    '6000 Oak Lane',
    '1000 Pine Road',
    '456 Maple Avenue',
    '789 Cedar Street',
    '321 Birch Road',
    '876 Walnut Street',
    '246 Elm Avenue',
]

function getRandomStreetA(seed: string) {
    const random = customSeededRandom(seed)
    const index = Math.floor(random() * streetAs.length)
    return streetAs[index]
}

function customSeededRandom(seed: string): () => number {
    const hashedSeed = hashCode(seed)
    return mulberry32(hashedSeed)
}

function hashCode(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
        hash = (hash << 5) - hash + str.charCodeAt(i)
        hash |= 0 // Convert to 32bit integer
    }
    return hash >>> 0 // Ensure non-negative integer
}

function mulberry32(a: number): () => number {
    return function () {
        let t = a + 0x6d2b79f5
        t = Math.imul(t ^ (t >>> 15), t | 1)
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296
    }
}
